import { themeEvents } from "../core/events";

const RUNTIME_KEY = "__rebalanceTimesactRuntime";

interface TimesactRuntime {
  cleanup: () => void;
}

interface TimesactRuntimeConfig {
  pageType?: string;
  shouldBootProduct?: boolean;
}

interface TimesactRuntimeWindow extends Window {
  [RUNTIME_KEY]?: TimesactRuntime;
  __rebalanceTimesactConfig?: TimesactRuntimeConfig;
}

function cleanupQuickViewTimesactArtifacts(): number {
  const quickViewContainer =
    document.querySelector<HTMLElement>(".quick-view-container") ??
    document.querySelector<HTMLElement>("#quick-view-content");
  if (!quickViewContainer) {
    return 0;
  }

  const staleNodes = quickViewContainer.querySelectorAll("#preorder-label, .timesact-preorder-description");
  staleNodes.forEach((node) => node.parentNode?.removeChild(node));
  return staleNodes.length;
}

function refreshTimesactRuntime(reason: string): boolean {
  const ta = window.ta;
  const invoked =
    ta && typeof ta.refreshCollection === "function"
      ? (ta.refreshCollection(), true)
      : ta && typeof ta.runCollection === "function"
        ? (ta.runCollection(), true)
        : cleanupQuickViewTimesactArtifacts() > 0;

  document.documentElement.setAttribute("data-build-timesact-adapter", "ready");
  document.documentElement.setAttribute(
    "data-build-timesact-last-refresh",
    `${reason}:${invoked ? "invoked" : "no-op"}`
  );
  window.dispatchEvent(
    new CustomEvent("rebalance:timesact:refresh", {
      detail: { reason, invoked }
    })
  );

  return invoked;
}

export function initTimesact(): void {
  const runtimeWindow = window as TimesactRuntimeWindow;
  const runtimeConfig = runtimeWindow.__rebalanceTimesactConfig;
  if (runtimeConfig?.pageType === "product" && runtimeConfig.shouldBootProduct === false) {
    return;
  }

  if (runtimeWindow[RUNTIME_KEY]) {
    return;
  }

  let refreshTimer = 0;

  const scheduleRefresh = (reason: string, delayMs = 220): void => {
    if (refreshTimer) {
      window.clearTimeout(refreshTimer);
    }

    refreshTimer = window.setTimeout(() => {
      refreshTimer = 0;
      refreshTimesactRuntime(reason);
    }, delayMs);
  };

  const handleGridUpdated = (): void => scheduleRefresh("collection-grid-updated");
  const handleAfterAdd = (): void => scheduleRefresh("cart-after-add");
  const unlistenRouteInitialized = themeEvents.on("route:initialized", ({ template }) => {
    scheduleRefresh(`route-initialized:${template}`);
  });

  document.addEventListener("baseline:collection:grid-updated", handleGridUpdated);
  document.body.addEventListener("baseline:modalcart:afteradditem", handleAfterAdd);

  runtimeWindow[RUNTIME_KEY] = {
    cleanup: () => {
      document.removeEventListener("baseline:collection:grid-updated", handleGridUpdated);
      document.body.removeEventListener("baseline:modalcart:afteradditem", handleAfterAdd);
      unlistenRouteInitialized();
      if (refreshTimer) {
        window.clearTimeout(refreshTimer);
        refreshTimer = 0;
      }
    }
  };

  scheduleRefresh("adapter-init", 0);
}
